import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Home, LifeBuoy, Settings2 } from "lucide-react";
import { useLets } from "@/hooks/useLets";
import { HELP_TOPICS, SEARCH_INDEX, matches } from "@/lib/searchIndex";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
/** ⌘K anywhere in the app opens it, as does the sidebar's search bar. */
export const useSearchShortcut = (setOpen) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
};
const Results = ({ query, go }) => {
  const { lets } = useLets();
  const groups = useMemo(() => {
    const entries = [
      ...SEARCH_INDEX.map((e) => ({ ...e, icon: Settings2 })),
      ...(lets || []).map((l) => ({ group: "Properties", title: l.address, to: `/lets/${l.id}/edit`, keywords: "property let", icon: Home })),
      ...HELP_TOPICS.map((t) => ({ group: "Help", title: t.title, to: `/help#${t.id}`, keywords: t.keywords, icon: LifeBuoy })),
    ];
    const hits = query.trim() ? entries.filter((e) => matches(e, query)) : entries.filter((e) => e.group !== "Help" && e.group !== "Properties");
    return hits.reduce((acc, e) => ({ ...acc, [e.group]: [...(acc[e.group] || []), e] }), {});
  }, [lets, query]);
  return (
    <CommandList className="max-h-[min(420px,60vh)] p-1.5">
      <CommandEmpty>Nothing matches "{query}".</CommandEmpty>
      {Object.entries(groups).map(([group, items]) => (
        <CommandGroup key={group} heading={group} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-muted-foreground">
          {items.map(({ title, to, icon: Icon }) => (
            <CommandItem key={to} value={to} onSelect={() => go(to)} className="group gap-2.5 rounded-md px-2 py-2" data-testid={`search-result-${to}`}>
              <Icon className="size-4 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{title}</span>
              <CornerDownLeft className="size-3.5 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" />
            </CommandItem>
          ))}
        </CommandGroup>
      ))}
    </CommandList>
  );
};
export const SearchPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  useEffect(() => { if (!open) setQuery(""); }, [open]);
  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[20%] translate-y-0 overflow-hidden p-0 sm:max-w-lg" data-testid="search-palette">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Find a page, a setting, a property or a help topic.</DialogDescription>
        <Command shouldFilter={false} className="rounded-lg">
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search pages, settings, properties and help" className="h-12" data-testid="search-input" />
          {open && <Results query={query} go={go} />}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
